/**
 * En-route ping cadence + bounded storage (pure).
 *
 * MORE GPS DATA (owner decision 2026-10-08): the 2026-10-08 field test
 * produced ~17 pings for a 12-minute drive at the old ~60 s cadence — far too
 * sparse for a smooth member bar or a useful route trace. While a journey is
 * active the device now pings on a DISTANCE-AND-TIME throttle: every
 * ~{@link JOURNEY_PING_MIN_INTERVAL_MS} *or* every
 * ~{@link JOURNEY_PING_MIN_DISTANCE_METERS}, whichever comes first, but only
 * for a fix that is FRESH and reasonably ACCURATE (a stale or coarse fix adds
 * noise, not information — and stale fixes re-sent as fresh is exactly the
 * bug #439 fixes).
 *
 * STORAGE: the journey lives on the session's DynamoDB row (400 KB item
 * limit, shared with the during-session `driveSegments` track). A stored ping
 * is ~100 bytes (lat/lng/timestamp/accuracy/stage/etaMinutes/locationSource/
 * cumulativeKm with attribute names), so {@link MAX_STORED_JOURNEY_PINGS}
 * (240) bounds the journey blob at ~25 KB. Beyond the cap the OLDER part of
 * the trace is DOWNSAMPLED by distance ({@link compactJourneyPings}) rather
 * than truncated — the route shape survives, gap endpoints are kept so
 * GPS-blind stretches stay visible, and `cumulativeKm` keeps the km sum
 * lossless regardless of which intermediate points are dropped.
 */

import { haversineKm, IMPLAUSIBLE_SPEED_KMH, MAX_SAMPLE_GAP_MS } from './km'
import type { GeoStamp, JourneyPing } from './types'

/* ------------------------------- cadence ---------------------------------- */

/** Time trigger: ping at least this often while a fresh fix is available. */
export const JOURNEY_PING_MIN_INTERVAL_MS = 20_000
/** Distance trigger: ping once the provider has moved this far since the last one. */
export const JOURNEY_PING_MIN_DISTANCE_METERS = 150
/** Hard floor between two pings so a burst of fixes can't flood the API. */
export const JOURNEY_PING_MIN_SPACING_MS = 5_000
/** A fix older than this (device timestamp vs now) is not "fresh". */
export const JOURNEY_PING_MAX_FIX_AGE_MS = 15_000
/** A fix coarser than this is not worth sending on the cadence. */
export const JOURNEY_PING_MAX_ACCURACY_METERS = 100

export type JourneyPingDecisionReason =
  | 'first'
  | 'interval'
  | 'distance'
  | 'stale_fix'
  | 'inaccurate'
  | 'too_soon'
  | 'not_due'

export interface JourneyPingDecision {
  send: boolean
  reason: JourneyPingDecisionReason
  /** Metres moved since the last sent ping (0 when there is none). */
  movedMeters: number
}

export interface JourneyPingCadenceInput {
  /** The fix just delivered by the position stream. */
  fix: GeoStamp
  /** The last ping actually sent (position + the time it was sent). */
  lastSent: { lat: number; lng: number; sentAt: number } | null
  now: number
}

/**
 * Decide whether a freshly delivered fix should go out as a `gps` ping.
 * Freshness and accuracy gates come first (a bad fix never pings, even when a
 * ping is overdue); then the first fix of a journey always pings; then the
 * time / distance triggers, subject to the hard spacing floor.
 */
export function evaluateJourneyPingCadence(input: JourneyPingCadenceInput): JourneyPingDecision {
  const { fix, lastSent, now } = input

  if (now - fix.timestamp > JOURNEY_PING_MAX_FIX_AGE_MS) {
    return { send: false, reason: 'stale_fix', movedMeters: 0 }
  }
  if (
    fix.accuracy == null ||
    !Number.isFinite(fix.accuracy) ||
    fix.accuracy < 0 ||
    fix.accuracy > JOURNEY_PING_MAX_ACCURACY_METERS
  ) {
    return { send: false, reason: 'inaccurate', movedMeters: 0 }
  }

  if (!lastSent) return { send: true, reason: 'first', movedMeters: 0 }

  const movedMeters = haversineKm(lastSent, fix) * 1000
  const sinceLast = now - lastSent.sentAt
  if (sinceLast < JOURNEY_PING_MIN_SPACING_MS) {
    return { send: false, reason: 'too_soon', movedMeters }
  }
  if (sinceLast >= JOURNEY_PING_MIN_INTERVAL_MS) {
    return { send: true, reason: 'interval', movedMeters }
  }
  if (movedMeters >= JOURNEY_PING_MIN_DISTANCE_METERS) {
    return { send: true, reason: 'distance', movedMeters }
  }
  return { send: false, reason: 'not_due', movedMeters }
}

/* ------------------------------- storage ---------------------------------- */

/**
 * Upper bound on pings stored per journey (~25 KB on the row). A 30-minute
 * urban drive at the new cadence produces ~90–130 pings, so the cap is only
 * reached on long drives, where compaction kicks in.
 */
export const MAX_STORED_JOURNEY_PINGS = 240

/** The most recent pings are always kept raw (live ETA needs full fidelity). */
export const JOURNEY_RECENT_PINGS_KEPT_RAW = 60

/** Initial distance between kept points when the older trace is downsampled. */
export const JOURNEY_TRACE_COMPACT_METERS = 100

/**
 * A silence between consecutive GPS pings longer than this is a GPS-blind
 * stretch: both endpoints are preserved by compaction and the trace map
 * draws the gap as "no GPS" rather than a straight line.
 */
export const JOURNEY_TRACE_GAP_MS = 2 * 60_000

function isGpsPing(ping: JourneyPing): boolean {
  return ping.locationSource !== 'estimate'
}

/**
 * Cumulative plausible driving km up to a new ping, carried from the previous
 * GPS ping. A pair is only credited when its implied speed and time gap are
 * sane (same bounds as the km module) — so a teleport after a GPS-blind
 * stretch adds nothing. Estimate pings reuse stale coordinates and just carry
 * the running total forward.
 */
export function cumulativeKmForNextPing(pings: readonly JourneyPing[], next: JourneyPing): number {
  let prevGps: JourneyPing | null = null
  for (let i = pings.length - 1; i >= 0; i--) {
    if (isGpsPing(pings[i])) {
      prevGps = pings[i]
      break
    }
  }
  const carried = prevGps?.cumulativeKm ?? lastCumulativeKm(pings)
  if (!prevGps || !isGpsPing(next)) return carried

  const dtMs = next.timestamp - prevGps.timestamp
  if (dtMs <= 0 || dtMs > MAX_SAMPLE_GAP_MS) return carried
  const km = haversineKm(prevGps, next)
  const speedKmh = km / (dtMs / 3_600_000)
  if (speedKmh > IMPLAUSIBLE_SPEED_KMH) return carried
  return carried + km
}

function lastCumulativeKm(pings: readonly JourneyPing[]): number {
  for (let i = pings.length - 1; i >= 0; i--) {
    const value = pings[i].cumulativeKm
    if (value != null && Number.isFinite(value)) return value
  }
  return 0
}

/** Total plausible driving km across the stored pings (lossless under compaction). */
export function journeyPingsTotalKm(pings: readonly JourneyPing[]): number {
  return lastCumulativeKm(pings)
}

/**
 * Downsample `pings` (an ordered run) so consecutive kept GPS points are at
 * least `spacingMeters` apart. Always keeps the first and last point and both
 * endpoints of any {@link JOURNEY_TRACE_GAP_MS} silence. Estimate pings are
 * dropped (no positional information).
 */
function downsampleRun(pings: readonly JourneyPing[], spacingMeters: number): JourneyPing[] {
  const gps = pings.filter(isGpsPing)
  if (gps.length <= 2) return gps
  const kept: JourneyPing[] = [gps[0]]
  for (let i = 1; i < gps.length - 1; i++) {
    const prev = gps[i - 1]
    const current = gps[i]
    const next = gps[i + 1]
    const last = kept[kept.length - 1]
    const gapBefore = current.timestamp - prev.timestamp > JOURNEY_TRACE_GAP_MS
    const gapAfter = next.timestamp - current.timestamp > JOURNEY_TRACE_GAP_MS
    if (gapBefore || gapAfter || haversineKm(last, current) * 1000 >= spacingMeters) {
      kept.push(current)
    }
  }
  kept.push(gps[gps.length - 1])
  return kept
}

/**
 * Bound a journey's stored pings to {@link MAX_STORED_JOURNEY_PINGS}.
 *
 * Under the cap the list is returned untouched. Over it, the most recent
 * {@link JOURNEY_RECENT_PINGS_KEPT_RAW} pings stay raw and the older part is
 * downsampled by distance, doubling the spacing until the whole list fits.
 * The first ping and the first accurate GPS fix are always retained. Idempotent.
 */
export function compactJourneyPings(pings: JourneyPing[]): JourneyPing[] {
  if (pings.length <= MAX_STORED_JOURNEY_PINGS) return pings

  const splitAt = Math.max(1, pings.length - JOURNEY_RECENT_PINGS_KEPT_RAW)
  const older = pings.slice(0, splitAt)
  const recent = pings.slice(splitAt)
  const budget = MAX_STORED_JOURNEY_PINGS - recent.length

  let spacing = JOURNEY_TRACE_COMPACT_METERS
  let compactedOlder = downsampleRun(older, spacing)
  // Each doubling roughly halves the kept count; a few rounds cover any
  // realistic drive. Stop once it fits or the spacing is absurd.
  while (compactedOlder.length > budget && spacing < 50_000) {
    spacing *= 2
    compactedOlder = downsampleRun(older, spacing)
  }
  // Anchors always survive: the first ping (route progress is measured from
  // it, even when it was an estimate) and the first accurate GPS fix — an
  // origin candidate for the member/follower lenses (journey-origin-privacy),
  // which must not lose the provider's real start on a long drive.
  const firstAccurateGps = older.find(
    (p) =>
      p.locationSource !== 'estimate' && p.accuracy != null && p.accuracy <= JOURNEY_PING_MAX_ACCURACY_METERS,
  )
  const anchors = new Set([pings[0], ...(firstAccurateGps ? [firstAccurateGps] : [])])
  let rest = compactedOlder.filter((p) => !anchors.has(p))
  // Pathological (e.g. all GPS-blind gaps): keep the anchors + the newest of
  // the older run to stay inside the bound.
  if (anchors.size + rest.length > budget) rest = rest.slice(rest.length - Math.max(0, budget - anchors.size))
  const kept = new Set([...anchors, ...rest])
  return [...older.filter((p) => kept.has(p)), ...recent]
}

function appendAtEnd(pings: JourneyPing[], ping: JourneyPing): JourneyPing[] {
  const stamped: JourneyPing = { ...ping, cumulativeKm: cumulativeKmForNextPing(pings, ping) }
  return compactJourneyPings([...pings, stamped])
}

/**
 * Add a ping to a journey's stored list: stamps `cumulativeKm` and applies
 * the storage bound. Server-side replacement for the old append-then-trim.
 * The list stays in timestamp order: a ping older than the newest stored one
 * (a late live ping, or a fix clamped to the receive time behind a skewed
 * earlier one) is inserted after the last ping at or before its time and the
 * pings after it are re-stamped. Equal timestamps keep arrival order.
 */
export function appendJourneyPing(pings: JourneyPing[], ping: JourneyPing): JourneyPing[] {
  let insertAt = pings.length
  while (insertAt > 0 && pings[insertAt - 1].timestamp > ping.timestamp) insertAt--
  if (insertAt === pings.length) return appendAtEnd(pings, ping)
  const tail = pings.slice(insertAt).map(({ cumulativeKm: _km, ...rest }) => rest)
  return [ping, ...tail].reduce<JourneyPing[]>((acc, p) => appendAtEnd(acc, p), pings.slice(0, insertAt))
}

/**
 * Conservative DynamoDB footprint of ONE stored ping (bytes): attribute names
 * lat(3) lng(3) timestamp(9) accuracy(8) stage(5) etaMinutes(10)
 * locationSource(14) cumulativeKm(12) source(7) ≈ 71, values ≈ 48, map overhead ≈ 3.
 */
export const JOURNEY_PING_APPROX_BYTES = 122

/** Rough DynamoDB footprint of a ping list (bytes) — item-size sanity checks. */
export function estimateJourneyPingsBytes(pings: readonly JourneyPing[]): number {
  return pings.length * JOURNEY_PING_APPROX_BYTES
}
